import moment from 'moment'
import React, { FC } from 'react'

//* Styles
import { LocaleCodes } from '../../../constants/localization.constants'
import { FeaturedResourceContainer } from './style'

//* Components
import GlobalContainer from '../globalContainer'
import { ImageContainer } from './subcomponents/imageContainer'
import { ResourceTexts } from './subcomponents/resourceTexts'

//* Helpers
import { ModuleNames } from '../../../constants/componentNames.constants'
import { ComponentFeaturedResource } from '../../../models/components/featuredResource'
import { isResourceCategory } from '../../../utils/functions.utils'
import { findTarget, getMicrocopyData } from '../../../utils/microcopy.utils'
import { renderRichText } from '../../../utils/richTextHandler.utils'

type Props = {
  data: ComponentFeaturedResource
  pageLocale: string
  pageType: string
}

const FeaturedResource: FC<Props> = ({ data, pageLocale, pageType }) => {
  if (!data) return null
  const cardDetail = data.card
    ? {
        heading: 'title' in data.card ? data.card.title : '',
        category: 'category' in data.card ? data.card.category : null,
        image: 'image' in data.card ? data.card.image?.image : null,
        spotlight: 'spotlight' in data.card ? data.card.spotlight?.video : null,
        inEnglish: data.card.inEnglish || false,
        seoMetadata: 'seoMetadata' in data.card ? data.card.seoMetadata : null,
        seo: 'seo' in data.card ? data.card.seo : null,
        microcopy: data.card.microcopy || null,
        date: data.card.date || '',
        richTextBody: 'body' in data.card && data.card?.body ? renderRichText(data.card.body) : '',
        teaserBody: 'teaserBody' in data.card ? data.card.teaserBody : '',
        blogTeaserBody:
          'blogTeaserBody' in data.card && data.card?.blogTeaserBody
            ? renderRichText(data.card.blogTeaserBody)
            : null,
        companyLogo: 'companyLogo' in data.card ? data.card.companyLogo : null,
        companyColorLogo: 'companyColorLogo' in data.card ? data.card.companyColorLogo : null,
        url1: 'url1' in data.card ? data.card.url1 : '',
        tcEyebrow: 'eyebrow' in data.card ? data.card.eyebrow : '',
        tcDescription: 'description' in data.card ? renderRichText(data.card.description) : '',
        tcCtaText: 'ctaText' in data.card ? data.card.ctaText : '',
        tcCtaLink: 'ctaLink' in data.card ? data.card.ctaLink : '',
        tcVideo: 'video' in data.card ? data.card.video : '',
      }
    : {}

  const resourceDetail = {
    theme: data.theme?.toLowerCase() || 'light',
    cta: data.seeAllCta || false,
    useCase: data.useCase?.toLowerCase() || '',
  }

  const images = {
    card: {
      url: cardDetail.image?.file?.url,
      altText: cardDetail.image?.altText,
    },
    spotlight: {
      url: cardDetail.spotlight?.image?.file?.url,
      altText: cardDetail.spotlight?.altText,
    },
    tinyFeaturedIcon: {
      url: cardDetail.category?.tinyFeaturedIcon?.image?.file?.url,
      altText: cardDetail.category?.tinyFeaturedIcon?.image?.altText,
    },
    colorImage: {
      url: cardDetail.category?.colorImage?.image?.file?.url,
      altText: cardDetail.category?.colorImage?.image?.altText,
    },
    companyLogo: {
      url: cardDetail.companyLogo?.image?.file?.url,
      altText: cardDetail.companyLogo?.image?.altText,
    },
    companyColorLogo: {
      url: cardDetail.companyColorLogo?.image?.file?.url,
      altText: cardDetail.companyColorLogo?.image?.altText,
    },
  }

  const pageIsEnglish = pageLocale === LocaleCodes.ENGLISH.code
  const seoUrl = cardDetail.seoMetadata?.pageCanonicalUrl || cardDetail.seo?.pageCanonicalUrl || ''

  const mainImageSrc = images.card.url || images.spotlight.url || ''
  const mainImageAlt = images.card.altText || images.spotlight.altText || 'UiPath'

  const longTeaser = data.longTeaser ? renderRichText(data.longTeaser) : ''
  const metaDescription =
    cardDetail.seoMetadata?.pageMetaDescription || cardDetail.seo?.pageMetaDescription
  const cardBody =
    longTeaser ||
    cardDetail.richTextBody ||
    cardDetail.teaserBody ||
    cardDetail.blogTeaserBody ||
    cardDetail.tcDescription ||
    metaDescription

  let categoryIconSrc = images.tinyFeaturedIcon.url || images.colorImage.url || ''
  let categoryIconAlt =
    images.tinyFeaturedIcon.altText || images.colorImage.altText || 'UiPath Image'

  const eyebrow = cardDetail.category?.title || cardDetail.tcEyebrow || ''
  const number = cardDetail.category?.number || ''
  if (data.card?.__typename === ModuleNames.PAGE_RESOURCE_DEMO && 'hero' in data.card) {
    cardDetail.heading = data.card.hero?.headline || cardDetail.heading || ''
  }

  let secondaryCtaTextVal, secondaryCtaUrlVal
  let primaryCta,
    secondaryCtaText,
    inEnglishText,
    secondaryCtaUrl: any = ''

  if (cardDetail.microcopy && number) {
    primaryCta = getMicrocopyData(cardDetail.microcopy, number, 'primary', 'text')
    secondaryCtaText = getMicrocopyData(cardDetail.microcopy, number, 'secondary', 'text')
    secondaryCtaUrl = getMicrocopyData(cardDetail.microcopy, number, 'secondary', 'url')

    if (cardDetail.microcopy?.values) {
      let microcopyPillValue = ''

      if (pageLocale && pageLocale !== LocaleCodes.ENGLISH.code) {
        const localeCodeShort = pageLocale.slice(-2)
        microcopyPillValue = findTarget(
          `resource.${localeCodeShort}.${number}.InEnglishPILL.text`,
          cardDetail.microcopy?.values
        )
      }

      inEnglishText = microcopyPillValue === '' ? 'In English' : microcopyPillValue
    }
  }

  if (secondaryCtaText && secondaryCtaText[0] && secondaryCtaText[0].value) {
    secondaryCtaTextVal = secondaryCtaText[0].value
  }

  if (secondaryCtaUrl && secondaryCtaUrl[0] && secondaryCtaUrl[0].value) {
    secondaryCtaUrlVal = secondaryCtaUrl[0].value
  }

  let convertedDate
  if (cardDetail.date) {
    convertedDate = new Date(cardDetail.date)
    cardDetail.date = moment(convertedDate).format('MMMM D, YYYY')
  }

  const isCategory = isResourceCategory('Webinar', number) || isResourceCategory('Events', number)
  let ctaText, when
  if (isCategory && primaryCta && primaryCta.length >= 2 && convertedDate) {
    let now = new Date()
    when = convertedDate >= now ? 'before' : 'after'

    if (primaryCta[0].key && primaryCta[0].key.includes(when)) {
      ctaText = primaryCta[0].value
    } else if (primaryCta[1].key && primaryCta[1].key.includes(when)) {
      ctaText = primaryCta[1].value
    }
  }

  //* If not webinar, get the first cta text value
  if (!ctaText && primaryCta && primaryCta[0] && primaryCta[0].value) ctaText = primaryCta[0].value
  const ImageContainerADD = (
    <ImageContainer
      mainImageSrc={mainImageSrc}
      mainImageAlt={mainImageAlt}
      cardDetail={cardDetail}
      pageIsEnglish={pageIsEnglish}
      inEnglishText={inEnglishText || ''}
      categoryIconAlt={categoryIconAlt}
      categoryIconSrc={categoryIconSrc}
      cardUrl={seoUrl || cardDetail.url1 || cardDetail.tcCtaLink || ''}
      images={images}
      video={cardDetail.tcVideo}
      useCase={resourceDetail.useCase}
    />
  )
  const ResourceTextsADD = (
    <ResourceTexts
      eyebrow={eyebrow}
      heading={cardDetail.heading || ''}
      cardBody={cardBody}
      cardUrl={seoUrl || cardDetail.url1 || cardDetail.tcCtaLink || ''}
      ctaText={ctaText || cardDetail.tcCtaText}
      resourceDetail={resourceDetail}
      secondaryCtaUrlVal={secondaryCtaUrlVal}
      secondaryCtaTextVal={secondaryCtaTextVal}
      video={cardDetail.tcVideo}
      useCase={resourceDetail.useCase}
    />
  )
  let useCaseToRender
  if (resourceDetail.useCase === 'full width') {
    useCaseToRender = (
      <>
        {ResourceTextsADD}
        {ImageContainerADD}
      </>
    )
  } else {
    useCaseToRender = (
      <>
        {ImageContainerADD}
        {ResourceTextsADD}
      </>
    )
  }

  const usecaselayout =
    resourceDetail.useCase === 'full width' || resourceDetail.useCase === 'square'
      ? 'usecaselayout'
      : ''
  return (
    <FeaturedResourceContainer
      $resourceTheme={resourceDetail.theme}
      $useCase={resourceDetail.useCase}
      $pageType={pageType}
      data-cy="FeaturedResource"
      className={`featuredResource ${resourceDetail.useCase.split(' ').join('')}`}>
      <GlobalContainer className={usecaselayout}>
        <div className="FeaturedResource__Inner">{useCaseToRender} </div>
      </GlobalContainer>
    </FeaturedResourceContainer>
  )
}

export default FeaturedResource
